import { FlexyHeaderForm, useModal } from '@europrocurement/flexy-components';
import { Box, Button, Grid, TextField } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { FormaliteDocument, formalitiesDocumentSelector } from '@europrocurement/l2d-domain';
import CheckboxListItem from '../../components/CheckboxListItem';

type DocumentItem = {
    id: number;
    label: string;
    checked: boolean;
    type: number;
};

type DocumentTypeFormProps = {
    title: string;
    type: number;
    itemsSelected: number[];
    handleSelectItems: (selectedItems: unknown) => void;
};

const DocumentTypeForm: React.FunctionComponent<DocumentTypeFormProps> = function ({
    title,
    type,
    itemsSelected,
    handleSelectItems,
}) {
    const dataSource = useSelector(formalitiesDocumentSelector);
    const { modalActions } = useModal();
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedItems, setSelectedItems] = useState<number[]>(itemsSelected);
    const [documentsItems, setDocumentsItems] = useState<DocumentItem[]>([]);

    useEffect(() => {
        setSelectedItems(itemsSelected);
    }, [itemsSelected, type]);

    const onCheckItem = (documentId: number) => {
        if (selectedItems.indexOf(documentId) === -1) {
            setSelectedItems([...selectedItems, documentId]);
        }
    };
    const onUncheckItem = (documentId: number) => {
        if (selectedItems.indexOf(documentId) !== -1) {
            setSelectedItems(selectedItems.filter((item) => item !== documentId));
        }
    };

    useEffect(() => {
        const items = dataSource.data
            .map((item: FormaliteDocument) => ({
                id: item.id,
                label: item.libelle,
                checked: selectedItems.indexOf(item.id as number) !== -1,
                type: item.type,
            }))
            .filter(
                (item: DocumentItem) =>
                    type === item.type &&
                    (!searchTerm || item.label.toLowerCase().match(searchTerm.toLowerCase())),
            )
            .sort((a: DocumentItem, b: DocumentItem) => {
                if (a.label.toLowerCase() < b.label.toLowerCase()) {
                    return -1;
                }

                if (a.label.toLowerCase() > b.label.toLowerCase()) {
                    return 1;
                }

                return 0;
            }) as DocumentItem[];

        setDocumentsItems(items);
    }, [dataSource, searchTerm, selectedItems, type]);

    return (
        <Box sx={{ width: '500px' }}>
            <Box sx={{ display: 'grid', gap: '10px' }}>
                <FlexyHeaderForm
                    outlined
                    label={title}
                    sx={{ marginTop: '-15px' }}
                />
                <TextField
                    fullWidth
                    placeholder="Filtrer la liste des documents"
                    onChange={(e) => {
                        setSearchTerm(e.target.value);
                    }}
                />
                {documentsItems.length < 1 ? (
                    <Box>Type de document non trouvé</Box>
                ) : (
                    <Box sx={{ maxHeight: '45vh', overflowY: 'scroll' }}>
                        <Grid
                            container
                            gap="10px 0"
                        >
                            {documentsItems.map((item: DocumentItem) => (
                                <Grid
                                    key={item.id}
                                    item
                                    style={{ width: '100%' }}
                                >
                                    <CheckboxListItem
                                        label={item.label}
                                        checked={item.checked}
                                        onUpdate={(isChecked: boolean) => {
                                            if (isChecked) {
                                                onCheckItem(item.id);
                                            } else {
                                                onUncheckItem(item.id);
                                            }
                                        }}
                                    />
                                </Grid>
                            ))}
                        </Grid>
                    </Box>
                )}

                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        paddingTop: '26px',
                    }}
                >
                    <Button
                        key="document-modal-cancel-btn"
                        color="primary"
                        onClick={() => modalActions.close()}
                        sx={{ padding: '16px 24px', fontSize: '16px' }}
                    >
                        Fermer
                    </Button>
                    {documentsItems.length > 0 && (
                        <Button
                            key="document-modal-submit-btn"
                            color="primary"
                            variant="contained"
                            onClick={() => handleSelectItems(selectedItems)}
                            sx={{ padding: '16px 24px', fontSize: '16px' }}
                        >
                            Confirmer
                        </Button>
                    )}
                </Box>
            </Box>
        </Box>
    );
};

export default DocumentTypeForm;
